import React, { useState } from 'react';
import { 
  Building2, 
  Wrench, 
  Megaphone, 
  Shield, 
  Droplets, 
  ClipboardCheck,
  Paintbrush,
  Home,
  Pipette,
  ArrowRight 
} from 'lucide-react';
import { servicesData } from '../mock';
import { detailedGallery } from '../detailedGalleryData';
import { Button } from './ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import MiniCarousel from './MiniCarousel';
import { ServiceModal } from './ServiceModal';
import { UrgentContactModal } from './UrgentContactModal';

const iconMap = {
  Building2,
  Wrench,
  Megaphone,
  Shield,
  Droplets,
  ClipboardCheck,
  Paintbrush,
  Home,
  Pipette
};

export const ServicesSection = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUrgentModalOpen, setIsUrgentModalOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState('');

  const getPhotosForService = (serviceId) => {
    const categoryMap = {
      'batiment': 'batiment',
      'peinture': 'peinture',
      'ouvragesTechniques': 'ouvragesTechniques',
      'evenementiel': 'evenementiel',
      'securite': 'securite',
      'nettoyage': 'nettoyage',
      'toiture': 'toiture',
      'descentes': 'descentes',
      'maintenance': 'maintenance'
    };
    
    const categoryKey = categoryMap[serviceId];
    if (!categoryKey || !detailedGallery[categoryKey]) return [];
    
    const category = detailedGallery[categoryKey];
    const photos = [];
    
    if (category.subcategories) {
      Object.values(category.subcategories).forEach(subcategory => {
        if (subcategory.images && Array.isArray(subcategory.images)) {
          photos.push(...subcategory.images);
        }
      });
    }
    
    return photos;
  };

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToGalleryWithFilter = (serviceId) => {
    // Mapping entre service ID et domaine de galerie
    const serviceToDomain = {
      'batiment': 'batiment',
      'ouvrages': 'ouvragesTechniques',
      'evenementiel': 'evenementiel',
      'securite': 'securite',
      'nettoyage': 'nettoyage',
      'maintenance': 'maintenance'
    };
    
    // Dispatch un événement custom pour changer le filtre de la galerie
    const domain = serviceToDomain[serviceId] || 'tous';
    window.dispatchEvent(new CustomEvent('filterGallery', { detail: { domain } }));
    
    // Scroll vers la galerie
    setTimeout(() => {
      document.querySelector('#galerie')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleServiceClick = (serviceId) => {
    // Ouvrir l'accordéon
    setOpenAccordion(serviceId);
    
    // Scroll vers la section des prestations détaillées
    setTimeout(() => {
      const accordion = document.querySelector(`[data-service-id="${serviceId}"]`);
      if (accordion) {
        accordion.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            Nos <span className="text-orange-600">Services</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Des solutions complètes pour tous vos travaux en hauteur et accès difficiles
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {servicesData.map((service) => {
            const Icon = iconMap[service.icon];
            return (
              <div
                key={service.id}
                onClick={() => {
                  setSelectedService(service);
                  setIsModalOpen(true);
                }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-orange-600 cursor-pointer"
              >
                <div className="bg-orange-600 text-white w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <Icon size={28} />
                </div>
                <h3 className="text-2xl font-bold text-black mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="flex items-center text-orange-600 font-semibold hover:gap-3 transition-all group">
                  <span>En savoir plus</span>
                  <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Service Modal */}
        <ServiceModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          service={selectedService}
          photos={selectedService ? getPhotosForService(selectedService.id) : []}
        />

        {/* Urgent Contact Modal */}
        <UrgentContactModal 
          isOpen={isUrgentModalOpen}
          onClose={() => setIsUrgentModalOpen(false)}
        />

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-12 text-center text-white shadow-2xl">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">
            Besoin d'une intervention rapide ?
          </h3>
          <p className="text-xl mb-8 opacity-90">
            Nous intervenons sous 24-48h en cas d'urgence
          </p>
          <Button
            onClick={() => setIsUrgentModalOpen(true)}
            size="lg"
            className="bg-white text-red-600 hover:bg-gray-100 text-lg px-8 py-6 font-bold"
          >
            Demande urgente
          </Button>
        </div>
      </div>
    </section>
  );
};
